import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import "../../../App.css";
import color from "../../../components/utils/Colors";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, FolderOpen } from "@mui/icons-material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import {
  insertDataInTable,
  updateByIdDataInTable,
} from "../../../services/services";
import { jwtDecode } from "jwt-decode";
import { useSnackbar } from "../../../components/utils/SnackBar";

const getUserIdFromToken = () => {
  try {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const decoded: any = jwtDecode(token);
      return decoded.id || decoded.userId || decoded.sub;
    }
  } catch (err) {
    console.error("Error decoding token:", err);
  }
  return null;
};

const QuestionFormPreview = () => {
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const location = useLocation();

  // location.state mein courseId bhi lo
  const {
    questions = [],
    questionType = "MCQ",
    lectureId = "",
    lectureName = "",
    courseId = "",
  } = (location.state as any) || {};

  const [assignedLectureId] = useState(lectureId);
  const [assignedCourseId] = useState(courseId);
  const [selectedQuestionType] = useState(questionType);
  const [loading, setLoading] = useState(false);
  const showSnackbar = useSnackbar();

  const handleGoBack = () => navigate(-1);
  const mapQuestionType = (type: string): string => {
    const map: Record<string, string> = {
      MCQ: "mcq",
      "Yes/No": "yes/no",
      "Short Questions": "short_answer",
      "Fill in the blanks": "fill_in_the_blank",
      // agar already lowercase aa raha hai tab bhi cover ho
      mcq: "mcq",
      "yes/no": "yes/no",
      short_answer: "short_answer",
      fill_in_the_blank: "fill_in_the_blank",
    };
    return map[type] || type;
  };
  const handleSave = async () => {
    setLoading(true);

    const createdBy = getUserIdFromToken();
    if (!createdBy) {
      showSnackbar("Unable to determine user. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      const mappedType = mapQuestionType(selectedQuestionType);

      for (const q of questions) {
        let answer = "";
        if (mappedType === "mcq") {
          answer =
            q.options?.[Number(q.correctOption)] || String(q.correctOption);
        } else if (mappedType === "yes/no") {
          answer = String(q.correctOption);
        } else {
          answer = q.correctAnswer || "";
        }

        const payload: any = {
          name: q.question,
          questionType: mappedType,
          contentId: Number(assignedLectureId),
          courseId: Number(assignedCourseId),
          answer,
          createdBy: parseInt(createdBy) || createdBy,
          status: "draft",
        };

        if (mappedType === "mcq") {
          payload.options = q.options;
          payload.correctOption = Number(q.correctOption);
        }

        console.log("Sending payload:", payload);
        await insertDataInTable("question", payload);
      }

      showSnackbar("Questions saved successfully!");
    } catch (err: any) {
      showSnackbar(
        err.response?.data?.message ||
          "An error occurred while saving questions.",
      );
      console.error("Save error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    const createdBy = getUserIdFromToken();
    if (!createdBy) {
      showSnackbar("Unable to determine user. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      const mappedType = mapQuestionType(selectedQuestionType);

      for (const q of questions) {
        let answer = "";
        if (mappedType === "mcq") {
          answer =
            q.options?.[Number(q.correctOption)] || String(q.correctOption);
        } else if (mappedType === "yes/no") {
          answer = String(q.correctOption);
        } else {
          answer = q.correctAnswer || "";
        }

        const payload: any = {
          name: q.question,
          questionType: mappedType,
          contentId: Number(assignedLectureId),
          courseId: Number(assignedCourseId),
          answer,
          createdBy: parseInt(createdBy) || createdBy,
          status: "assigned",
        };

        if (mappedType === "mcq") {
          payload.options = q.options;
          payload.correctOption = Number(q.correctOption);
        }
        await insertDataInTable("question", payload);
      }
      showSnackbar("Questions saved successfully!");
      navigate("/course-info");
    } catch (err: any) {
      showSnackbar(
        err.response?.data?.message ||
          "An error occurred while saving questions.",
      );
      console.error("Save error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        marginTop: isSmallScreen ? "25px" : "30px",
        flex: 1,
        borderRadius: "16px",
        paddingBottom: "5%",
        p: 2,
      }}
    >
      <Typography
        onClick={handleGoBack}
        sx={{
          margin: 0,
          position: "absolute",
          top: isSmallScreen ? "120px" : "140px",
          color: color.firstColor,
          fontSize: "16px",
          fontWeight: "bold",
          left: isSmallScreen ? 10 : 60,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <ChevronLeft /> Back
      </Typography>

      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: "bold",
          textAlign: "center",
          color: color.textColor,
          width: "fit-content",
          margin: "auto",
          padding: "2px 10px",
          borderRadius: "4px",
          mb: 6,
        }}
      >
        Question Result Screen
      </Typography>

      <Box
        sx={{
          background: color.secondColor,
          p: 2,
          pt: 4,
          borderRadius: 4,
          boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.09)",
        }}
      >
        {/* ── Summary row ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: isSmallScreen ? "column-reverse" : "row",
            gap: 2,
            p: isSmallScreen ? 0 : 2,
          }}
        >
          <Typography sx={{ color: color.firstColor, mr: "auto" }}>
            {questions.length} Question{questions.length !== 1 ? "s" : ""}{" "}
            created for <strong>{lectureName}</strong>
          </Typography>
          <Button
            id="button1"
            style={{ gap: "5px", marginLeft: "auto", borderRadius: "80px" }}
          >
            <FolderOpen /> My Collection
          </Button>
        </Box>

        {/* ── Questions List ── */}
        {questions.map((question: any, index: number) => (
          <Box
            key={index}
            sx={{
              borderRadius: "8px",
              padding: "16px",
              paddingBottom: 3,
              position: "relative",
              border: "solid 2px",
              borderColor: color.borderColor,
              mt: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                mb: 2,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  color: color.textColor,
                  fontSize: "20px",
                  fontWeight: "bold",
                }}
              >
                {`Question : ${index + 1}`}
              </Typography>
              <Button
                id="button1"
                style={{
                  borderRadius: "80px",
                  fontWeight: "normal",
                  fontSize: isSmallScreen ? "10px" : "14px",
                }}
              >
                + Save to collection
              </Button>
            </Box>

            <Typography sx={{ mb: 2, color: color.textColor, fontWeight: 500 }}>
              {question.question}
            </Typography>

            {/* MCQ options */}
            {selectedQuestionType === "MCQ" && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  gap: "16px",
                  mt: 1,
                }}
              >
                {question.options?.map((opt: string, optionIndex: number) => (
                  <Box
                    key={optionIndex}
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      width: isSmallScreen ? "100%" : "45%",
                      background:
                        String(question.correctOption) === String(optionIndex)
                          ? "rgba(0, 128, 0, 0.15)"
                          : "transparent",
                      borderRadius: "5px",
                      padding: "5px",
                    }}
                  >
                    <span
                      style={{
                        marginRight: 8,
                        background:
                          String(question.correctOption) === String(optionIndex)
                            ? "darkgreen"
                            : color.firstColor,
                        borderRadius: "50%",
                        width: "30px",
                        height: "30px",
                        textAlign: "center",
                        paddingTop: "3.5px",
                        color: "white",
                        flexShrink: 0,
                      }}
                    >
                      {String.fromCharCode(65 + optionIndex)}
                    </span>
                    <Typography sx={{ flex: 1 }}>{opt}</Typography>
                    {String(question.correctOption) === String(optionIndex) && (
                      <CheckCircle style={{ color: "green", marginLeft: 4 }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Yes/No options */}
            {selectedQuestionType === "Yes/No" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  mt: 1,
                }}
              >
                {["Yes", "No"].map((opt, optionIndex) => (
                  <Box
                    key={optionIndex}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: isSmallScreen ? "100%" : "30%",
                      background:
                        question.correctOption?.toLowerCase() ===
                        opt.toLowerCase()
                          ? "rgba(0, 128, 0, 0.15)"
                          : "transparent",
                      borderRadius: "5px",
                      padding: "5px",
                    }}
                  >
                    <span
                      style={{
                        marginRight: 8,
                        background:
                          question.correctOption?.toLowerCase() ===
                          opt.toLowerCase()
                            ? "darkgreen"
                            : color.firstColor,
                        borderRadius: "50%",
                        width: "30px",
                        height: "30px",
                        textAlign: "center",
                        paddingTop: "3.5px",
                        color: "white",
                        flexShrink: 0,
                      }}
                    >
                      {String.fromCharCode(65 + optionIndex)}
                    </span>
                    <Typography sx={{ flex: 1 }}>{opt}</Typography>
                    {question.correctOption?.toLowerCase() ===
                      opt.toLowerCase() && (
                      <CheckCircle style={{ color: "green", marginLeft: 4 }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Short / Fill in blanks answer */}
            {(selectedQuestionType === "Short Questions" ||
              selectedQuestionType === "Fill in the blanks") && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: "8px",
                  background: "rgba(0,128,0,0.08)",
                  border: "1px solid rgba(0,128,0,0.3)",
                }}
              >
                <Typography
                  sx={{ color: "green", fontWeight: "bold", mb: 0.5 }}
                >
                  Correct Answer:
                </Typography>
                <Typography>{question.correctAnswer}</Typography>
              </Box>
            )}
          </Box>
        ))}

        {/* ── Actions ── */}
        <Box
          mt={4}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
          }}
        >
          <Button
            id="button"
            sx={{ marginLeft: "16px" }}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                Saving...
              </Box>
            ) : (
              "Save"
            )}
          </Button>

          <Button
            id="button1"
            sx={{ marginLeft: "16px" }}
            onClick={handleAssign}
            disabled={loading}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                Assigning...
              </Box>
            ) : (
              "Assign"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionFormPreview;
